import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  endorsementCatalog,
  type EndorsementChangeInput,
  type EndorsementDetail,
  type EndorsementDraftInput,
  type PolicyDetail,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatMoney,
  moneyInputError,
  moneyToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';

import {
  EndorsementCoinsurersCard,
  EndorsementPremiumEstimateCard,
} from './EndorsementFinancialFields';
import { EndorsementInfo, EndorsementPolicyDetails } from './EndorsementPolicyDetails';

/**
 * Passo "Informacoes do endosso" dos endossos com efeito financeiro (alteracao,
 * cancelamento e aniversario; END-050 a END-063).
 *
 * Os campos existem e o rascunho os guarda, mas nenhum calculo roda: o premio do
 * endosso, a restituicao e o reajuste da IS sao regra pendente (P-END-2, P-PAR-3). O
 * prazo em dias tambem nao e calculado (PRC-008).
 *
 * P-END-2: a data de cancelamento (END-075), as cosseguradoras (END-057), o premio
 * estimado com o valor solicitado e a revisao de valor (END-064 a END-071).
 */

// O texto do campo sai dos digitos do inteiro, sem ponto flutuante (achado B46 da
// revisao das ondas 5 e 6).
function centavosEmTexto(centavos: number | undefined): string {
  if (centavos === undefined) return '';
  return moneyToInput({ amount: centavos, currency: 'BRL', exponent: 2 }) || '0';
}

export function FinancialEndorsementStep({
  policy,
  endorsement,
  editavel,
  podePedir = editavel,
  salvando,
  onProximo,
}: {
  policy: PolicyDetail;
  endorsement: EndorsementDetail;
  editavel: boolean;
  podePedir?: boolean;
  salvando: boolean;
  onProximo: (input: EndorsementDraftInput | null) => void;
}) {
  const codigo = endorsement.endorsement_type?.code ?? 'alteracao';
  const alteracoes = new Map(
    (endorsement.changes ?? []).map((item) => [item.field, item.after] as const),
  );

  const [novaIS, setNovaIS] = useState(
    centavosEmTexto(alteracoes.get('insured_amount')?.amount?.amount),
  );
  const [inicio, setInicio] = useState(alteracoes.get('coverage_start')?.date ?? '');
  const [fim, setFim] = useState(alteracoes.get('coverage_end')?.date ?? '');
  const [novaComplementar, setNovaComplementar] = useState(
    centavosEmTexto(alteracoes.get('complementary_insured_amount')?.amount?.amount),
  );
  const [modo, setModo] = useState<'' | 'dados' | 'texto'>(
    endorsement.object_edit_mode ?? '',
  );
  const [objeto, setObjeto] = useState(endorsement.object_text ?? '');
  const [condicao, setCondicao] = useState(
    alteracoes.get('particular_clause')?.text ?? '',
  );
  // Endosso financeiro (P-END-2): data de cancelamento e valor solicitado.
  const [dataDeCancelamento, setDataDeCancelamento] = useState(
    endorsement.financial?.effective_date ?? '',
  );
  const [valorSolicitado, setValorSolicitado] = useState(
    centavosEmTexto(endorsement.financial?.requested_premium?.amount),
  );

  const objetoDaApolice = useQuery({
    queryKey: ['policy-object', policy.id],
    queryFn: ({ signal }) => endorsementCatalog.policyObject(policy.id, signal),
    retry: false,
  });

  /** Os campos do endosso financeiro que a operacao nao tem (P-END-2). */
  function camposFinanceiros(): EndorsementDraftInput {
    if (!endorsement.financial) return {};
    const campos: EndorsementDraftInput = {
      requested_premium:
        valorSolicitado.trim() === '' ? null : toMoney(valorSolicitado),
    };
    if (codigo === 'cancelamento') {
      campos.effective_date = dataDeCancelamento === '' ? null : dataDeCancelamento;
    }
    return campos;
  }

  function montar(): EndorsementDraftInput {
    const changes: EndorsementChangeInput[] = [];
    if (novaIS.trim() !== '') {
      changes.push({
        scope: 'apolice',
        field: 'insured_amount',
        after: { amount: toMoney(novaIS) },
      });
    }
    if (inicio !== '') {
      changes.push({
        scope: 'apolice',
        field: 'coverage_start',
        after: { date: inicio },
      });
    }
    if (fim !== '') {
      changes.push({ scope: 'apolice', field: 'coverage_end', after: { date: fim } });
    }
    if (novaComplementar.trim() !== '') {
      changes.push({
        scope: 'apolice',
        field: 'complementary_insured_amount',
        after: { amount: toMoney(novaComplementar) },
      });
    }
    if (condicao.trim() !== '') {
      changes.push({
        scope: 'apolice',
        field: 'particular_clause',
        after: { text: condicao },
      });
    }
    return {
      changes,
      object_edit_mode: modo,
      object_text: modo === 'texto' && objeto.trim() !== '' ? objeto : null,
      ...camposFinanceiros(),
    };
  }

  // O valor que nao converte nao vai ao servidor como zero: o campo mostra o erro e o
  // passo nao segue (achado B46 da revisao das ondas 5 e 6).
  const valoresInvalidos =
    (endorsement.financial !== null &&
      endorsement.financial !== undefined &&
      moneyInputError(valorSolicitado) !== undefined) ||
    (codigo !== 'cancelamento' &&
      (moneyInputError(novaIS) !== undefined ||
        moneyInputError(novaComplementar) !== undefined));

  return (
    <div className="flex flex-col gap-4">
      <EndorsementPolicyDetails policy={policy} />

      {codigo === 'cancelamento' && endorsement.financial ? (
        <Card>
          <CardHeader
            title="Dados do cancelamento"
            description="A data em que a garantia deixa de valer, dentro da vigencia da apolice."
          />
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Data de cancelamento"
              type="date"
              disabled={!editavel}
              min={policy.coverage_start}
              max={policy.coverage_end}
              value={dataDeCancelamento}
              onChange={(event) => setDataDeCancelamento(event.target.value)}
            />
            <EndorsementInfo
              rotulo="Vigencia da apolice"
              valor={`${formatCivilDate(policy.coverage_start)} - ${formatCivilDate(policy.coverage_end)}`}
            />
          </CardBody>
        </Card>
      ) : null}

      {endorsement.financial ? <EndorsementCoinsurersCard policy={policy} /> : null}

      <Card>
        <CardHeader
          title="Objeto da apolice"
          description="Dados do objeto da apolice inicial. O objeto alterado so muda a apolice quando o calculo do endosso entrar."
        />
        <CardBody className="flex flex-col gap-3">
          <p className="whitespace-pre-wrap text-sm text-ink-900">
            {objetoDaApolice.data?.text || 'Nao informado'}
          </p>
          {codigo === 'alteracao' ? (
            <>
              <Select
                label="Modo de edicao do objeto"
                disabled={!editavel}
                placeholder="Sem alteracao do objeto"
                options={[
                  { value: 'dados', label: 'Editar apenas os dados selecionados' },
                  { value: 'texto', label: 'Editar todo texto do objeto' },
                ]}
                value={modo}
                onChange={(event) =>
                  setModo(event.target.value as '' | 'dados' | 'texto')
                }
              />
              {modo === 'dados' ? (
                <Alert tone="info" title="Editar dados do objeto da apolice">
                  Nao existem dados passiveis de ajuste no objeto da apolice desta
                  cotacao: a montagem do texto com as TAGs ainda e regra pendente
                  (END-008).
                </Alert>
              ) : null}
              {modo === 'texto' ? (
                <Textarea
                  label="Editar texto do objeto"
                  disabled={!editavel}
                  placeholder="Digite seu texto aqui..."
                  value={objeto}
                  onChange={(event) => setObjeto(event.target.value)}
                />
              ) : null}
            </>
          ) : null}
        </CardBody>
      </Card>

      {codigo === 'alteracao' || codigo === 'aniversario' ? (
        <Card>
          <CardHeader
            title="Valores"
            description="Coluna Apolice atual, para comparacao, e a apolice nova pedida."
          />
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <EndorsementInfo
              rotulo="Apolice atual - IS"
              valor={formatMoney(policy.insured_amount)}
            />
            <MoneyField
              label="Valor IS modalidade principal"
              disabled={!editavel}
              value={novaIS}
              error={moneyInputError(novaIS)}
              onValueChange={setNovaIS}
            />
            <EndorsementInfo
              rotulo="Apolice atual - vigencia"
              valor={`${formatCivilDate(policy.coverage_start)} - ${formatCivilDate(policy.coverage_end)}`}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Inicio da vigencia"
                type="date"
                disabled={!editavel}
                value={inicio}
                onChange={(event) => setInicio(event.target.value)}
              />
              <Field
                label="Final da vigencia"
                type="date"
                disabled={!editavel}
                value={fim}
                onChange={(event) => setFim(event.target.value)}
              />
            </div>
            <EndorsementInfo
              rotulo="Apolice atual - IS complementar"
              valor={
                policy.complementary_insured_amount
                  ? formatMoney(policy.complementary_insured_amount)
                  : 'Nao definido'
              }
            />
            <MoneyField
              label="Valor IS modalidade complementar"
              disabled={!editavel}
              value={novaComplementar}
              error={moneyInputError(novaComplementar)}
              onValueChange={setNovaComplementar}
            />
            <Textarea
              label="Condicao particular"
              disabled={!editavel}
              value={condicao}
              onChange={(event) => setCondicao(event.target.value)}
            />
          </CardBody>
        </Card>
      ) : null}

      {endorsement.financial ? (
        <EndorsementPremiumEstimateCard
          endorsement={endorsement}
          editavel={editavel}
          podePedir={podePedir}
          valorSolicitado={valorSolicitado}
          onValorSolicitado={setValorSolicitado}
        />
      ) : (
        <Alert tone="warning" title="Premio Estimado">
          {codigo === 'cancelamento'
            ? 'O premio estimado a restituir depende da regra de restituicao, que ainda nao foi homologada (END-005, PRC-010). O valor aparece quando a regra entrar.'
            : 'O novo calculo do premio estimado e baseado na diferenca entre o valor da apolice vigente e a politica da seguradora, e essa regra ainda nao foi homologada (PRC-Q). O valor aparece quando a regra entrar.'}
        </Alert>
      )}

      {editavel ? (
        <div className="flex justify-end">
          <Button
            loading={salvando}
            disabled={valoresInvalidos}
            onClick={() =>
              onProximo(
                codigo === 'cancelamento'
                  ? endorsement.financial
                    ? camposFinanceiros()
                    : null
                  : montar(),
              )
            }
          >
            Proximo
          </Button>
        </div>
      ) : null}
    </div>
  );
}
